/**
 * Slowly scrolls a list so that a screen recording shows every participant.
 * It pauses at the bottom, then glides back to the top. When someone scrolls
 * by hand (or code calls `scrollTo`), it waits a moment before carrying on from there.
 * Off under prefers-reduced-motion.
 */
import { prefersReducedMotion } from './dom.js';

const LOOP_SECONDS = 40; // aim to pass the whole list in this time…
const MIN_SPEED = 16; // …but never crawl (px/s)…
const MAX_SPEED = 60; // …or rush past names faster than people can read them
const EDGE_PAUSE_MS = 2500;
const MANUAL_PAUSE_MS = 3500;

export function createAutoScroll(node) {
  let raf = 0;
  let last = 0;
  let pos = 0;
  let holdUntil = 0;

  function frame(now) {
    raf = requestAnimationFrame(frame);
    const dt = last ? Math.min(100, now - last) : 0;
    last = now;

    const max = node.scrollHeight - node.clientHeight;
    if (Math.abs(node.scrollTop - pos) > 2) {
      // Moved by someone else: follow it and let it settle.
      pos = node.scrollTop;
      holdUntil = Math.max(holdUntil, now + MANUAL_PAUSE_MS);
    }
    if (max <= 1 || now < holdUntil) return;

    if (pos >= max - 1) {
      node.scrollTo({ top: 0, behavior: 'smooth' }); // handled as a manual scroll above
      holdUntil = now + EDGE_PAUSE_MS;
      return;
    }
    const speed = Math.min(MAX_SPEED, Math.max(MIN_SPEED, max / LOOP_SECONDS));
    pos = Math.min(max, pos + (speed * dt) / 1000);
    node.scrollTop = pos;
    if (pos >= max - 1) holdUntil = now + EDGE_PAUSE_MS;
  }

  return {
    start() {
      if (raf || prefersReducedMotion()) return;
      last = 0;
      pos = node.scrollTop;
      holdUntil = performance.now() + EDGE_PAUSE_MS;
      raf = requestAnimationFrame(frame);
    },
    stop() {
      cancelAnimationFrame(raf);
      raf = 0;
    },
    reset() {
      node.scrollTop = 0;
      pos = 0;
    },
  };
}
